/**
 * The shell's finite state — ADR-006 §13.2: the mode is APP state, not Document
 * state. The test that decides every "where does this live?" argument: does it
 * change what the work IS? Document. Does it describe how you are currently
 * looking at it? App state. Mode and selection are the latter. But app state
 * earns the same discipline: ONE enum, ONE value, so v2's independent-boolean
 * bug — `isBindPoseMode && ikEnabled && isPlaying` was a representable state,
 * defended by ad-hoc guards at each use (§1.3) — is UNREPRESENTABLE here, not
 * merely guarded against. Selection speaks Document vocabulary — bone ids,
 * never live object references — which is what lets it survive a re-import
 * instead of dangling into a discarded graph (§1.3 row 4).
 *
 * A pure value + pure transitions; the store holds it (ADR-007 §4) and undo
 * never touches it (§3.3 — app state does not move as a side effect of undo).
 * Depends on: nothing (engine-free — session-no-engine).
 * Depended on by: the app store, the shell surface when it lands.
 */

/**
 * The modes the shell offers (brief §5.4). Exactly one is active at a time —
 * the single source of truth that replaces v2's colliding booleans. The FSM
 * drives which panels and tools the top bar offers (§13.2); this v1 fixes the
 * enum, not yet the per-mode tool sets (those land with their surfaces).
 */
export const SHELL_MODES = ['edit', 'playback', 'pose', 'retarget-review'] as const;
export type ShellMode = (typeof SHELL_MODES)[number];

export interface ShellState {
  readonly mode: ShellMode;
  /**
   * Selected bones, addressed by Document id (§13.2) — never object refs, so a
   * selection outlives the graph it was made against. A set expressed as an
   * order-preserving list.
   */
  readonly selection: readonly string[];
  /**
   * The authored clip on stage, by NAME (§13.4) — or null for the Document's
   * default presentation (the first clip selection). View state by the §13.2
   * test: it changes how you are LOOKING at the work, not what the work is —
   * so undo never moves it (§3.3), and a name undo has orphaned resolves
   * against the CURRENT Document at projection time, never dangles into a
   * discarded graph.
   */
  readonly activeClip: string | null;
  /**
   * A LIBRARY clip previewed on the subject (ADR-033 phase 4), by World's
   * clipId — or null. View state like `activeClip`: it changes what the
   * stage plays, never what the work is; a pin (`package.set`) is the
   * Document write, this is the look before it. Wins over `activeClip` while
   * set: the person asked to hear this one.
   */
  readonly libraryClip: string | null;
}

/** Narrow an untrusted string (a DOM `data-mode`, a probe arg) to a mode. */
export function isShellMode(raw: string): raw is ShellMode {
  return (SHELL_MODES as readonly string[]).includes(raw);
}

/** A fresh sitting opens looking at the work — playback, nothing selected. */
export function initialShell(): ShellState {
  return { mode: 'playback', selection: [], activeClip: null, libraryClip: null };
}

/** Preview a library clip by clipId, or null to stop and return to the staged clip. */
export function previewLibraryClip(shell: ShellState, clipId: string | null): ShellState {
  return shell.libraryClip === clipId ? shell : { ...shell, libraryClip: clipId };
}

/** Put an authored clip on stage by name, or null for the default presentation. */
export function selectClip(shell: ShellState, name: string | null): ShellState {
  return shell.activeClip === name ? shell : { ...shell, activeClip: name };
}

/**
 * Move to a mode. A mode outside the enum refuses loudly (§8: never a silent
 * default to "some mode") — the guard exists because callers include the DOM,
 * whose strings TypeScript cannot vouch for.
 */
export function setMode(shell: ShellState, mode: ShellMode): ShellState {
  if (!isShellMode(mode)) {
    throw new Error(`unknown shell mode "${mode}" — the shell speaks: ${SHELL_MODES.join(', ')} (ADR-006 §13.2).`);
  }
  return shell.mode === mode ? shell : { ...shell, mode };
}

/** Replace the selection with these bone ids, de-duped and order-preserving. */
export function select(shell: ShellState, boneIds: readonly string[]): ShellState {
  const seen = new Set<string>();
  const unique = boneIds.filter((id) => {
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  return { ...shell, selection: unique };
}

export function clearSelection(shell: ShellState): ShellState {
  return shell.selection.length === 0 ? shell : { ...shell, selection: [] };
}
